import { action, computed, observable, set } from 'mobx';
import type { IAutomationInstance } from '~/types/automation.types';
import {
    IEvent,
    IWorkspace,
    IFeed,
    IWorkspaceAttachments,
    IFileAsset,
    IBookmark,
    IImageAsset
} from '~/types/model.types';
import {
    IMember,
    IPreferredVendorsListResponse,
    ITag,
    IWorkspaceFileSummaryResponse,
    IWorkspaceFlowResponse,
    IWorkspacePipelineDataResponse,
    IWorkspaceScheduledSessionResponse,
    IWorkspaceNotificationResponse,
    IWorkspaceMemberResponse,
    IWorkspaceTabInfoResponse
} from '~/types/server.types';
import type { User } from '~/models/user.model';
import type { Company } from '~/models/company.model';
import {
    WorkspaceSourceType,
    WorkspaceFileTypeToCd,
    WorkspaceOnboardingBotState,
    WorkspaceType
} from '~/types/enums.types';
import {
    TChangeWorkspacePipelineStageResponse,
    TWorkspaceCreator
} from '~/apps/main/Workspace/server-types';
import { CompanyStore } from '~/stores/company.store';
import { IUserGetter } from '~/stores/base-user.store';
import { localStorageKeys } from '~/common/storage.constants';
import { IWorkspaceActionSuggestion } from '~/apps/main/Pipeline/pipeline.types';

export class Workspace implements IWorkspace {
    @observable _id: string;
    @observable active_state: boolean;
    @observable attachments?: IWorkspaceAttachments;
    @observable company_id: string;
    created_at: string;
    creator: TWorkspaceCreator;
    creator_account_id: string;
    creator_company_brand_color: string;
    creator_company_id: string;
    creator_id: string;
    @observable deleted_members: IMember[];
    disable_client_pvl_requests: boolean | null;
    dismissed_feed_pvl_request: boolean | null;
    @observable event: IEvent;
    event_name: string;
    @observable feed: IFeed;
    @observable flows: IWorkspaceFlowResponse[];
    has_paid_payments: boolean;
    has_sent_files: boolean;
    has_signed_files: boolean;
    @observable members: IMember[];
    new_lead_email?: string;
    onboarding_bot_actions?: unknown;
    onboarding_bot_state?: WorkspaceOnboardingBotState;
    preferred_vendors_lists: IPreferredVendorsListResponse[];
    remove_action: string;
    referral_type?: string;
    @observable companyStore: CompanyStore;
    @observable userStore: IUserGetter;
    scheduled_sessions: IWorkspaceScheduledSessionResponse[];
    sent_link_on?: string;
    sessions_with_unrelated_member: string[];
    show_archived_files: boolean;
    tags?: ITag[];
    user_ids?: string[];
    vendor_id?: string;
    @observable workflow_id: string;
    @observable workflow_name: string;
    @observable workspace_files: IWorkspaceFileSummaryResponse[];
    workspace_notifications: IWorkspaceNotificationResponse[];
    @observable workspace_pipeline_data: IWorkspacePipelineDataResponse;
    @observable workspace_source: WorkspaceSourceType;
    workspace_status_cd: number;
    workspace_status_name: string;
    workspace_status_type: string;
    workspace_tab_info: IWorkspaceTabInfoResponse;
    workspace_tasks_overdue_count?: number;
    workspace_type: WorkspaceType | null;
    automations_mode: string;
    action_suggestions?: IWorkspaceActionSuggestion[];
    @observable lastAddedMemberId: string | null = null;

    constructor(
        workspace: IWorkspace,
        companyStore: CompanyStore,
        userStore: IUserGetter
    ) {
        this.companyStore = companyStore;
        this.userStore = userStore;
        this.lastAddedMemberId = localStorage.getItem(
            localStorageKeys.LAST_ADDED_MEMBER_STORAGE_KEY_PREFIX +
                workspace._id
        );
        set(this, workspace);
    }

    @action.bound
    setFeed(feed: IFeed) {
        this.feed = feed;
    }

    @action.bound
    setWorkspaceSource(workspace_source: WorkspaceSourceType) {
        this.workspace_source = workspace_source;
    }

    @action.bound
    addMember(member: IMember) {
        if (
            this.members.some(
                existingMember => existingMember._id === member._id
            )
        ) {
            return;
        }
        this.members.push(member as IWorkspaceMemberResponse);
        this.setLastAddedMember(member._id);
    }

    @action.bound
    updateMember(memberId: string, member: IMember) {
        const index = this.members.findIndex(item => item._id === memberId);

        if (index === -1) {
            return;
        }

        this.members[index] = {
            ...this.members[index],
            ...member
        };
    }

    @action.bound
    updateMembers(members: IMember[]) {
        this.members = members;
        this.setLastAddedMember(null);
    }

    @action.bound
    removeMember(memberId: string) {
        this.members = this.members.filter(member => member._id !== memberId);
        this.setLastAddedMember(null);
    }

    @computed
    get membersWithoutCurrUser() {
        const { currUser } = this.userStore;
        return this.members.filter(member => member._id !== currUser._id);
    }

    @action.bound
    isTeamMember(member: IMember) {
        return this.company_id === member?.company?._id;
    }

    @action.bound
    canAddParticipant(user: User, company: Company) {
        return (
            user.isVendor() &&
            company._id === this.company_id &&
            !this.isViewOnlyMode(user, company) &&
            !this.isArchived
        );
    }

    @action.bound
    isViewOnlyMode(user: User, company: Company) {
        if (!user.isVendor()) {
            return false;
        }

        const alreadyInWorkspace =
            this.members.filter(curr => {
                return curr._id === user._id;
            }).length > 0;

        // Collaborators are workspace participants but they are not part of the workspace company
        const isCollaborator = this.company_id !== company._id;

        if (alreadyInWorkspace && !isCollaborator) {
            return false;
        }

        const superAdminOfCreatorCompany =
            this.company_id === company._id &&
            user.hasCompanySuperAdminPermissions;
        return !superAdminOfCreatorCompany;
    }

    @action.bound
    isViewOnlyModeWithCollaborator(user: User, company: Company) {
        if (!user.isVendor()) {
            return false;
        }

        const alreadyInWorkspace =
            this.members.filter(curr => {
                return curr._id === user._id;
            }).length > 0;

        if (alreadyInWorkspace) {
            return false;
        }

        const superAdminOfCreatorCompany =
            this.company_id === company._id &&
            user.hasCompanySuperAdminPermissions;
        return !superAdminOfCreatorCompany;
    }

    @computed
    get allowWorkspaceActions() {
        const { currUser } = this.userStore;
        const { currCompany } = this.companyStore;

        return (
            currUser.isVendor() &&
            !this.isArchived &&
            !this.isViewOnlyMode(currUser, currCompany)
        );
    }

    @computed
    get firstActionSuggestion() {
        return this.action_suggestions?.[0];
    }

    @action.bound
    setIsActive(isActive) {
        this.active_state = isActive;
    }

    @computed
    get isArchived() {
        return !this.active_state;
    }

    // backwards compatibility for when workspace was passed from angular to component like EmailViewerFeedItem
    @computed
    get is_archived() {
        return !this.active_state;
    }

    @computed
    get isClientOrCollaborator() {
        return this.companyStore.currCompany?._id !== this.company_id;
    }

    @computed
    get isInWorkspaceCompany() {
        const { currUser } = this.userStore;
        return !currUser.isClient() && currUser.company_id === this.company_id;
    }

    @computed
    get isClientMode() {
        const { currUser } = this.userStore;
        return currUser.isClient() || currUser.company_id !== this.company_id;
    }

    @computed
    get workspaceOwner() {
        return this.members.find(member => member._id === this.creator_id);
    }

    @computed
    get membersMap() {
        const map = new Map();
        this.members.forEach(member => {
            map.set(member._id, member);
        });
        return map;
    }

    @computed
    get isWorkspaceOwner() {
        const { currUser } = this.userStore;
        return this.creator_id === currUser._id;
    }

    @computed
    get membersWithoutOwner() {
        const { currCompany } = this.companyStore;

        return this.members.filter(
            member =>
                !!member.email &&
                member._id !== currCompany?.owner?._id &&
                !this.isTeamMember(member)
        );
    }

    @computed
    get firstClient() {
        return this.membersWithoutOwner[0];
    }

    @computed
    get clients() {
        return this.members.filter(
            member => member.system_user_type === 'client'
        );
    }

    @action.bound
    updateWorkspaceAutomation(automation?: IAutomationInstance) {
        this.workflow_id = automation?._id || null;
        this.workflow_name = automation?.title || null;
    }

    @computed
    get anyPayableAndSentFlowsInWorkspace() {
        if (!this.flows) {
            return false;
        }
        return this.flows.some(function (flow) {
            return flow.status !== 'draft' && flow.has_invoice;
        });
    }

    @computed
    get anyPayableFilesInWorkspace() {
        return this.workspace_files?.some(function (file) {
            return (
                //TODO move to WorkspaceFile model
                file.file_type_cd === WorkspaceFileTypeToCd.proposal ||
                file.file_type === 'proposal' ||
                file.file_type_cd === WorkspaceFileTypeToCd.invoice ||
                file.file_type === 'invoice'
            );
        });
    }

    @computed
    get totalAttachments() {
        if (!this.attachments) {
            return 0;
        }

        return (
            this.attachments?.images?.length + this.attachments?.files?.length
        );
    }

    @computed
    get uploadEnabled() {
        return this.totalAttachments < 100;
    }

    @action.bound
    setWorkspacePipelineData(data: TChangeWorkspacePipelineStageResponse) {
        this.active_state = data.active_state;
        this.workspace_pipeline_data = data.workspace_pipeline_data;
    }

    @action.bound
    setPreferredVendorLists(pvls: IPreferredVendorsListResponse[]) {
        this.preferred_vendors_lists = pvls;
    }

    @action.bound
    addPreferredVendorLists(pvls: IPreferredVendorsListResponse[]) {
        this.preferred_vendors_lists = [
            ...(this.preferred_vendors_lists || []),
            ...pvls
        ];
    }

    addImagesAttachmentsToTheBeginning(images: IImageAsset[]) {
        if (!this.attachments || !this.attachments?.images) {
            this.attachments = {
                images,
                ...(this.attachments || {})
            };
        }

        this.attachments.images.unshift(...images);
    }

    addFilesAttachmentsToTheBeginning(files: IFileAsset[]) {
        if (!this.attachments || !this.attachments?.files) {
            this.attachments = {
                files,
                ...(this.attachments || {})
            };
        }

        this.attachments.files.unshift(...files);
    }

    @action.bound
    setAttachments(attachments: IWorkspaceAttachments) {
        this.attachments = attachments;
    }

    @action.bound
    updateBookmark(bookmark: IBookmark) {
        this.attachments.bookmarks = this.attachments.bookmarks.map(_bookmark =>
            _bookmark._id === bookmark._id ? bookmark : _bookmark
        );
    }

    @action.bound
    deleteBookmark(bookmarkId: string) {
        this.attachments.bookmarks = this.attachments.bookmarks.filter(
            bookmark => bookmark._id !== bookmarkId
        );
    }

    @action.bound
    setWorkspaceFiles(workspaceFiles: IWorkspaceFileSummaryResponse[]) {
        this.workspace_files = workspaceFiles;
    }

    @action.bound
    deleteImageAttachment(imageId: string) {
        this.attachments.images =
            this.attachments?.images?.filter(image => image._id !== imageId) ||
            [];
    }

    @action.bound
    deleteFileAttachment(fileId: string) {
        this.attachments.files =
            this.attachments?.files?.filter(file => file._id !== fileId) || [];
    }

    @action.bound
    updateWorkspaceFile(workspaceFile: IWorkspaceFileSummaryResponse) {
        const index = this.workspace_files.findIndex(
            file => file._id === workspaceFile._id
        );

        if (index === -1) {
            return;
        }

        this.workspace_files[index] = {
            ...this.workspace_files[index],
            ...workspaceFile
        };
    }

    @action.bound
    deleteWorkspaceFile(workspaceFileId: string) {
        this.workspace_files = this.workspace_files.filter(
            file => file._id === workspaceFileId
        );
    }

    @action.bound
    setLastAddedMember(memberId: string | null) {
        if (memberId) {
            localStorage.setItem(
                localStorageKeys.LAST_ADDED_MEMBER_STORAGE_KEY_PREFIX +
                    this._id,
                memberId
            );
            this.lastAddedMemberId = memberId;
        } else {
            localStorage.removeItem(
                localStorageKeys.LAST_ADDED_MEMBER_STORAGE_KEY_PREFIX + this._id
            );
            this.lastAddedMemberId = null;
        }
    }

    get orderedLegacyFiles() {
        return this.workspace_files.slice().reverse();
    }

    get hasPvl() {
        return (
            this.preferred_vendors_lists &&
            this.preferred_vendors_lists.length > 0
        );
    }
}
